import { useCallback, useEffect, useState } from 'react'
import {
  adminArchiveHeadline,
  adminSetHeadlineShared,
  createHeadline,
  HEADLINE_MAX,
  listHeadlines,
  updateHeadline,
  type Headline,
} from '../lib/headlinesApi'
import { shopDate } from '../lib/grouping'
import HeaderAction from './HeaderAction'

interface Props {
  token: string
  isAdmin: boolean
  /** Admin only: hand a headline to the issue form, pre-filled and linked. */
  onMakeIssue: (headline: Headline) => void
  /** Admin only: hand a headline to the task form, pre-filled and linked. */
  onCreateTask?: (headline: Headline) => void
  /**
   * Desktop row layout — adds `card--row` to each card (not while it is
   * being edited, when the textarea wants the full width). The rules live
   * inside the desktop media query, so phones are unaffected.
   */
  compact?: boolean
  /** Bump to make the list re-fetch (Meeting mode, when the presenter acts). */
  refreshKey?: number
}

/**
 * Headlines — what gets read out at the L10 meeting.
 *
 * Cards are deliberately flat, with no expand/collapse: a headline is one line
 * by construction (the database caps it), so there is nothing to hide behind a
 * tap. That is the visible difference from the Issues tab, and it is the point
 * — if something needs more room than this, it wants to be an issue.
 */
export default function Headlines({
  token,
  isAdmin,
  onMakeIssue,
  onCreateTask,
  compact = false,
  refreshKey = 0,
}: Props) {
  const [items, setItems] = useState<Headline[] | null>(null)
  const [writing, setWriting] = useState(false)
  const [draft, setDraft] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [sharedOpen, setSharedOpen] = useState(false)
  // Which card is being corrected, and the text so far.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  const load = useCallback(async () => {
    try {
      setItems(await listHeadlines(token, showArchived))
    } catch (err) {
      setItems([])
      setProblem(err instanceof Error ? err.message : String(err))
    }
  }, [token, showArchived])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true)
    setProblem(null)
    try {
      await fn()
      await load()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const submit = () =>
    guard(async () => {
      await createHeadline(token, draft)
      setDraft('')
      setWriting(false)
      setDone('Submission successful.')
    })

  if (items === null) return <p className="muted">Loading…</p>

  // --------------------------------------------------------------- write form
  if (writing) {
    const trimmed = draft.trim()
    const left = HEADLINE_MAX - draft.length
    const valid = trimmed !== '' && left >= 0

    return (
      <section className="form">
        <h2 className="form__title">Share a headline</h2>
        <p className="form__lead muted">
          Good news or bad, about a customer or someone here — one line, read out at
          the next meeting. If it needs discussing rather than just telling, report it
          as an issue instead.
        </p>

        {problem && <p className="banner">{problem}</p>}

        <label className="field">
          <span className="field__label">What is the news?</span>
          <textarea
            className="field__input"
            rows={3}
            autoFocus
            value={draft}
            placeholder="e.g. The Tesla owner from Tuesday sent a thank-you note about the bushing repair"
            onChange={(e) => setDraft(e.target.value)}
          />
          <span className={'field__hint' + (left < 0 ? ' field__hint--over' : '')}>
            {left < 0
              ? `${Math.abs(left)} characters too long — this probably wants to be an issue.`
              : `${left} characters left`}
          </span>
        </label>

        <div className="form__actions">
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              setWriting(false)
              setProblem(null)
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !valid}
            onClick={() => void submit()}
          >
            {busy ? 'Sharing…' : 'Share it'}
          </button>
        </div>
      </section>
    )
  }

  // --------------------------------------------------------------------- list
  const card = (h: Headline) => {
    // Author or admin. The database re-checks with the same rule.
    const canEdit = h.mine || isAdmin
    const editing = editingId === h.id
    const left = HEADLINE_MAX - editBody.length

    return (
    <article
      key={h.id}
      className={
        'card headline' +
        (compact && !editing ? ' card--row' : '') +
        (h.shared || h.archived ? ' card--done' : '')
      }
    >
      {editing ? (
        <div className="note">
          <textarea
            className="field__input"
            rows={3}
            autoFocus
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
          />
          <span className={'field__hint' + (left < 0 ? ' field__hint--over' : '')}>
            {left < 0 ? `${Math.abs(left)} characters too long` : `${left} characters left`}
          </span>
          <div className="note__actions">
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setEditingId(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy || editBody.trim() === '' || left < 0}
              onClick={() =>
                void guard(async () => {
                  await updateHeadline(token, h.id, editBody)
                  setEditingId(null)
                })
              }
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <p className="headline__body">{h.body}</p>
      )}

      <p className="headline__meta">
        {h.mine ? 'You' : h.created_by_name}
        {' · '}
        Created {shopDate(h.created_at)}
        {h.archived && <span className="chip chip--low">Archived</span>}
      </p>

      {h.issue_title && (
        <p className="card__brief card__brief--from">
          Became the issue “{h.issue_title}”
        </p>
      )}

      {h.task_title && (
        <p className="card__brief card__brief--from">
          Became the task “{h.task_title}”
        </p>
      )}

      {canEdit && !editing && (
        <div className="card__actions">
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy}
            onClick={() => {
              setEditBody(h.body)
              setEditingId(h.id)
            }}
          >
            Edit
          </button>
          {isAdmin && (
            <>
              <button
                type="button"
                className="btn btn--quiet"
                disabled={busy}
                onClick={() =>
                  void guard(async () => {
                    await adminSetHeadlineShared(token, h.id, !h.shared)
                  })
                }
              >
                {h.shared ? 'Not shared yet' : 'Mark shared'}
              </button>
          {/* Once it is an issue there is nothing left to drop down. */}
          {!h.issue_id && (
            <button
              type="button"
              className="btn btn--quiet"
              disabled={busy}
              onClick={() => onMakeIssue(h)}
            >
              Create an issue
            </button>
          )}
          {/* Same for a task: once there is one, there is nothing left to create. */}
          {onCreateTask && !h.task_id && (
            <button
              type="button"
              className="btn btn--quiet"
              disabled={busy}
              onClick={() => onCreateTask(h)}
            >
              Create a task
            </button>
          )}
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy}
            onClick={() =>
              void guard(async () => {
                await adminArchiveHeadline(token, h.id, !h.archived)
              })
            }
          >
            {h.archived ? 'Restore' : 'Archive'}
          </button>
          </>
          )}
        </div>
      )}
    </article>
    )
  }

  const toShare = items.filter((h) => !h.shared)
  const shared = items.filter((h) => h.shared)

  return (
    <section>
      <HeaderAction>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setDraft('')
            setDone(null)
            setWriting(true)
          }}
        >
          + Share a headline
        </button>
      </HeaderAction>

      {done && <p className="banner banner--ok">{done}</p>}
      {problem && <p className="banner">{problem}</p>}

      {isAdmin && (
        <label className="check check--inline">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span className="muted">Show archived</span>
        </label>
      )}

      {items.length === 0 && (
        <div className="panel panel--flat">
          <p className="muted">
            Nothing yet. Anything worth telling the team at the next meeting — a
            customer’s reaction, someone’s good week — goes here.
          </p>
        </div>
      )}

      {toShare.length > 0 && (
        <section className="group">
          <h2 className="group__label group__label--today">
            To share
            <span className="group__count">{toShare.length}</span>
          </h2>
          {toShare.map(card)}
        </section>
      )}

      {/* Folded away: once read out, a headline is history. Left open the list
          would be almost entirely past meetings within a month. */}
      {shared.length > 0 && (
        <div className="fold">
          <button
            type="button"
            className="fold__head"
            aria-expanded={sharedOpen}
            onClick={() => setSharedOpen((o) => !o)}
          >
            <span className="fold__caret" aria-hidden="true">
              {sharedOpen ? '▾' : '▸'}
            </span>
            Already shared
            <span className="group__count">{shared.length}</span>
          </button>
          {sharedOpen && <div className="fold__body">{shared.map(card)}</div>}
        </div>
      )}
    </section>
  )
}
